import React, { useRef, useState, useEffect } from 'react';
import { ChevronArrowDown, ChevronArrowUp, Close } from '../../../assets/icons';
import { DropdownOptions } from '../../../utilities/commonUtilities';
import { useAppSelector } from '../../../store/hooks/StoreHooks';

export interface DropdownProps {
  options: DropdownOptions[];
  label?: string;
  onSelect: (option: any) => void;
  placeholder?: string;
  error?: string;
  bgColor?: string;
  dropdownBgColor?: string;
  width?: string;
  selected?: string;
  required?: boolean;
  showCloseIcon?: boolean;
  showSelectOption?: boolean;  // New prop to control displaying "Select Option"
  height?: string;
  autoClose?: boolean; // Add optional prop for auto-closing behavior
  className?: string;
  border?: boolean;
  dropdownWidth?: string;
  disabled?: boolean;
  overrideEnableFlag?: boolean; // New prop to override enable flag behavior
}

const Dropdown: React.FC<DropdownProps> = ({
  options,
  label,
  onSelect,
  placeholder = "Select an option",
  error,
  bgColor,
  dropdownBgColor,
  width,
  selected,
  required,
  showCloseIcon = false,
  showSelectOption = false,
  height = "",
  autoClose = true, // Default to true for backward compatibility
  border = false,
  dropdownWidth,
  disabled = false,
  overrideEnableFlag = false, // Default to false
  ...props
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  // Derive selectedOption directly from props
  const selectedOption = selected && typeof selected === 'string' && options?.length > 0
    ? options?.find(option =>
      (typeof option?.id === 'string' && option.id.toLocaleLowerCase() === selected.toLocaleLowerCase()) ||
      option?.name === selected
    )?.name || null
    : null;


  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDropdown = (event: React.MouseEvent) => {
    if (disabled) return;
    event.preventDefault();
    event.stopPropagation();
    setIsOpen(!isOpen);
  };

  const handleOptionClick = (option: DropdownOptions, e: React.MouseEvent<HTMLLIElement>) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    onSelect(option);
    setIsOpen(false);
  };

  const optionsToDisplay = showSelectOption ? [{ id: "", name: 'Select' }, ...(options || [])] : (options || []);

  const { customerEnabled, customerCreated} = useAppSelector((state) => state.auth);
  const { projectReadOnly } = useAppSelector((state) => state.projectHeader);

  return (
    <>
      {
        <div
          className={`relative flex flex-col gap-[6px] w-full rounded-[8px] ${bgColor ? bgColor : 'bg-darkcolor-700'} ${props.className}`}
          ref={dropdownRef}
          style={{ width: width }}
        >
          {label && <label className="flex flex-row justify-start items-start text-neutral-25">
            {label}
            {required && <span className="text-error-400 ml-1">*</span>}
          </label>}
          <div className="relative border-darkcolor-400 border-[1px] rounded-[8px]"
          >
          <button
            type="button"
            onClick={toggleDropdown}
            disabled={overrideEnableFlag ? false : ((customerEnabled && !customerCreated) || projectReadOnly || disabled)}
            className={`flex justify-between items-center gap-[8px] w-full py-2 px-4 rounded-[8px] text-neutral-25 focus:outline-none whitespace-nowrap overflow-hidden text-ellipsis ${((customerEnabled || disabled) && !overrideEnableFlag) ? "cursor-not-allowed" : ""}`}
          >
            <span className={`overflow-hidden text-ellipsis ${((customerEnabled && !customerCreated) || projectReadOnly || disabled) && !overrideEnableFlag ? "text-neutral-400 cursor-not-allowed" : ""}`}>
              {selectedOption || placeholder}
            </span>
            {isOpen ? (
              <ChevronArrowUp
                color={`${((customerEnabled && !customerCreated) || projectReadOnly || disabled) && !overrideEnableFlag ? "#A3A3A3" : "#FFFFFF"}`}
                size={15}
                className="relative top-[3px] cursor-pointer"
              />
            ) : (
              <ChevronArrowDown
                size={15}
                color={`${((customerEnabled && !customerCreated) || projectReadOnly || disabled) && !overrideEnableFlag ? "#A3A3A3" : "#FFFFFF"}`}
                className="relative bottom-[3px] cursor-pointer"
              />
            )}
            {showCloseIcon && selectedOption && (
              <Close
                onClick={(e: React.MouseEvent<HTMLElement>) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onSelect({ id: '', name: '' });
                }}
              />
            )}
          </button>

            {isOpen && optionsToDisplay?.length > 0 && (
              <ul
                style={{ height: height, width: dropdownWidth }}
                className={`absolute z-[1000] mt-0.5 w-full border border-darkcolor-400 rounded-md shadow-lg overflow-auto hover:rounded-t-[8px] ${dropdownBgColor}`}
                {...props}
              >
                {optionsToDisplay.map((option) => (
                  <li
                    key={option?.id}
                    onClick={(e) => handleOptionClick(option, e)}
                    className={`cursor-pointer py-2 px-4 hover:bg-darkcolor-600 break-word
                  ${selected && selected?.toLocaleLowerCase() === option.id?.toLocaleLowerCase() ||
                        selected && selected?.toLocaleLowerCase() === option?.name?.toLocaleLowerCase()
                        ? 'text-primary-400'
                        : 'text-neutral-25'}`}
                  >
                    {option?.name}
                  </li>
                ))}
              </ul>
            )}
          </div>
          {error && <p className="text-error-500 text-sm">{error}</p>}
        </div>
      }
    </>
  );
};

export default Dropdown;
